// ============================================================
// The map's ground, and the small pure answers the panel needs
// ============================================================

import { groupByArrival } from '../../engine/scenes/cut';
import { groundLevel } from '../../engine/battlefield/excavation';
import { unprojectToPlane, projectGround, SCENE_VIEW } from '../../engine/battlefield/projection';
import { clampToBoundary, tileOf } from '../../engine/battlefield/boundary';
import { tokenForSize } from '../../engine/battlefield/tokens';
import { projectDefender } from '../../engine/roster/projection';

/** Metres per tile, never zero - the engine's (engine/battlefield/boundary). */
export { tileOf };

/** The height of the ground at a point in metres, off the terrain's voxel grid. */
export function groundAtFor(terrain, boundary) {
  const grid = terrain?.voxels;
  return (x, z) => groundLevel(grid, Math.round(x / tileOf(boundary)), Math.round(z / tileOf(boundary)));
}

/**
 * The point on the ground under a pointer event, in metres, or null.
 *
 * The same closed-form unprojection the marker drag and the deploy marker
 * use - a structure is placed where you pointed, not at the corner of the
 * cell you pointed into.
 */
export function groundPointOn(e, camera, boundary) {
  const svg = e.currentTarget?.ownerSVGElement || e.currentTarget;
  if (!svg?.getBoundingClientRect) return null;
  const rect = svg.getBoundingClientRect();
  const scale = Math.min(rect.width / SCENE_VIEW.width, rect.height / SCENE_VIEW.height);
  const x = (e.clientX - (rect.x + (rect.width - SCENE_VIEW.width * scale) / 2)) / scale;
  const y = (e.clientY - (rect.y + (rect.height - SCENE_VIEW.height * scale) / 2)) / scale;
  const at = unprojectToPlane(x, y, 0, camera, SCENE_VIEW);
  return at ? clampToBoundary(boundary, at.x, at.z) : null;
}

/**
 * The view from where a combatant stands.
 *
 * Not a true first-person projection - the scene is a fixed orthographic
 * viewBox and always will be - but the camera drops to their eye height
 * and comes in close, which answers the question people actually ask
 * across a table: "what can HE see from there?" Facing is gone (v8.29.0),
 * so the camera keeps its own bearing.
 */
export function lookFrom(camera, token, minPitch) {
  return {
    ...camera,
    target: { x: token.x, z: token.z },
    yaw: camera.yaw,
    pitch: minPitch,
    zoom: Math.min(Math.max(camera.zoom * 1.6, 24), 80),
  };
}

/**
 * Does the ring fit inside the arena? Said out loud rather than
 * discovered: a radius larger than the map spirals every slot into the
 * boundary and quietly stacks everybody on its edge, which looks like a
 * bug and is merely arithmetic.
 */
export function capacityWarningOf(deployment, boundary) {
  if (!deployment) return null;
  const reach = boundary.radius * Math.min(boundary.scaleX, boundary.scaleZ);
  if (deployment.ring.radius > reach) return `The ring is wider than the map (${reach.toFixed(0)} m).`;
  return null;
}

/**
 * Stand combatants who have just joined the roster at the deploy marker.
 *
 * A gesture that places people cannot place them itself: they are not on
 * the roster yet, and joining the roster is what brings a token into
 * being. So the gesture leaves a note in `arrivalPlan` - a point, or a
 * line - and this reads it once and forgets it, which is how a line and a
 * ring share every other rule (v8.22.0).
 *
 * Returns false when there is no marker, which is what lets the blind
 * ring stay as the fallback for a session that never planted one.
 */
export function deployArrivalsAt(arrivals, { deployment, deployment_, arrivalPlan, tile, onStampTeam, teamPoints }) {
  if (!deployment) return false;
  const fresh = (people) => people.map((c) => tokenForSize(c.id, projectDefender(c)?.size, { rev: 1 }));
  const plan = arrivalPlan.current;
  arrivalPlan.current = null;
  if (plan?.at)
    deployment_.deployThese(
      fresh(arrivals),
      arrivals.map(() => plan.at),
    );
  else if (plan?.from) deployment_.deployAlong(fresh(arrivals), plan.from, plan.to, tile);
  // A team with its own arrival point rings there; everyone else at the marker (plan scene-editor-library, B).
  else
    for (const { marker, people } of groupByArrival(arrivals, deployment, teamPoints))
      deployment_.deployThese(fresh(people), undefined, marker);
  // THE STAMP (v8.315.0). Whoever arrives while the flag carries a team
  // joins it - an ordinary sheet write, the same one the editor makes, so
  // it syncs and travels like any team.
  if (deployment.team && onStampTeam) arrivals.forEach((c) => onStampTeam(c.id, deployment.team));
  return true;
}

/**
 * Register a window keydown listener; the return value removes it, which
 * is exactly what an effect's cleanup wants.
 */
export function onWindowKey(handler) {
  window.addEventListener('keydown', handler);
  return () => window.removeEventListener('keydown', handler);
}

/**
 * Is the caret in a field? Delete belongs to the field with the caret in
 * it before it belongs to the map.
 */
export function isTyping() {
  const tag = document.activeElement?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * A heavy landing's kick (K3) as a CSS shift of the whole scene: the screen offset the kick's ground vector makes,
 * as a share of the view. A CSS transform moves the hit-test and every client rectangle with the picture, so a
 * click during the hit-stop lands where it looks (the Track K review), and nothing is re-projected.
 */
export function kickStyleOf(camera, kick) {
  if (!kick) return undefined;
  const from = projectGround(camera.target, camera);
  const to = projectGround({ x: camera.target.x + kick.dx, z: camera.target.z + kick.dz }, camera);
  const x = ((from.x - to.x) / SCENE_VIEW.width) * 100;
  const y = ((from.y - to.y) / SCENE_VIEW.height) * 100;
  return { transform: `translate(${x.toFixed(2)}%, ${y.toFixed(2)}%)` };
}
